import { BooleanInput, coerceBooleanProperty, coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import { Component, Input, OnChanges, SimpleChanges, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { RememberService } from '@app/core/services/remember.service';
import { BehaviorSubject, Observable, Subject, Subscription, debounceTime } from 'rxjs';

@Component({
    selector: 'tt-side-drawer',
    exportAs: 'sideDrawer',
    templateUrl: './side-drawer.component.html',
    styleUrls: ['./side-drawer.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class SideDrawerComponent implements OnChanges {
    @Input()
    public ttRememberId = '';

    @Input()
    public get ttRememberDebounceTime(): number {
        return this._rememberDebounceTime;
    }
    public set ttRememberDebounceTime(value: NumberInput) {
        this._rememberDebounceTime = coerceNumberProperty(value);
    }
    private _rememberDebounceTime = 150;

    /**
     * @default true
     */
    @Input()
    public set ttPersistLocalOnly(value: BooleanInput) {
        this._persistLocalOnly = coerceBooleanProperty(value);
    }
    public get ttPersistLocalOnly(): boolean {
        return this._persistLocalOnly;
    }
    private _persistLocalOnly = true;

    /**
     * The poisiton of the drawer.
     *
     * @default 'start'
     */
    @Input()
    public ttPosition: 'start' | 'end' = 'start';

    /**
     * How the drawer should be rendered.
     *
     * - `'side'` - pushes main content aside, both drawer and main content can be interacted with.
     * - `'over'` - main content keeps its size and a view is added on top to the respective position. Only drawer can be interacted with.
     * - `'push'` - pushes main content aside, but only drawer can be interacted with. Any attempt interaction with main content will close the drawer (unless `ttDisableClose` it `true`).
     *
     * @default 'side'
     */
    @Input()
    public ttMode: 'side' | 'over' | 'push' = 'over';

    /**
     * The width of the drawer, if not given the drawer size to its content.
     */
    @Input()
    public ttWidth?: `${number}px` | `${number}%` | `${number}rem` | `${number}em` | `${number}vw`;

    /**
     * Whether to disable default close logic (esc key or backdrop click).
     *
     * @default false
     */
    @Input()
    public set ttDisableClose(value: BooleanInput) {
        this._disableClose = coerceBooleanProperty(value);
    }
    public get ttDisableClose(): boolean {
        return this._disableClose;
    }
    private _disableClose: boolean = false;

    /**
     * Whether the drawer should initialise as opened or close.
     *
     * @default 'close'
     */
    @Input()
    public ttInitialState: 'open' | 'close' = 'close';

    @ViewChild(TemplateRef)
    public templateRef!: TemplateRef<any>;

    public stateSubject: BehaviorSubject<boolean>;

    public state$: Observable<boolean>;

    public remember$ = new Subject<void>();

    public rememberSubscription?: Subscription;

    constructor(private remember: RememberService) {
        this.stateSubject = new BehaviorSubject(this.ttInitialState === 'open');
        this.state$ = this.stateSubject.asObservable();
    }

    public open(): Promise<'open' | 'close'> {
        console.log('open');
        if (!!this.matDrawerRef) {
            return this.matDrawerRef?.open();
        }
        throw Error('SideDrawerComponent not setup correctly.');
    }

    public close(): Promise<'open' | 'close'> {
        if (!!this.matDrawerRef) {
            return this.matDrawerRef?.close();
        }
        throw Error('SideDrawerComponent not setup correctly.');
    }

    public async toggle(): Promise<'open' | 'close'> {
        if (!!this.matDrawerRef) {
            return this.matDrawerRef?.toggle();
        }
        throw Error('SideDrawerComponent not setup correctly.');
    }

    private async init() {
        await this.getAndSetLastUsedState();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ttRememberDebounceTime']) {
            this.ttRememberDebounceTime = changes['ttRememberDebounceTime'].currentValue;
        }

        if (changes['ttDisableClose']) {
            this.ttDisableClose = changes['ttDisableClose'].currentValue;
        }

        if (changes['ttRememberId'] || changes['ttRememberDebounceTime']) {
            if (!!this.rememberSubscription) {
                this.rememberSubscription.unsubscribe();
                delete this.rememberSubscription;
            }

            if (!!this.ttRememberId) {
                this.rememberSubscription = this.remember$.pipe(debounceTime(this.ttRememberDebounceTime)).subscribe({ next: () => this.rememberState() });
            }
        }
    }

    /**
     * Reference to the matdrawer ref this side drawer is connected to.
     */
    private matDrawerRef?: MatDrawer;

    /**
     * Sets this drawer components matdrawer ref to the given mat drawer.
     *
     * @param drawer the mat drawer for this drawer.
     */
    public async setMatDrawerRef(drawer: MatDrawer) {
        await this.init();
        this.matDrawerRef = drawer;
        if (this.matDrawerRef.opened) {
            this.stateSubject.next(true);
        }
        this.matDrawerRef.openedStart.subscribe({ next: () => this.stateSubject.next(true) });
        this.matDrawerRef.closedStart.subscribe({ next: () => this.stateSubject.next(false) });
        this.stateSubject.subscribe({
            next: (state) => {
                this.remember$.next();
            },
        });
    }

    private rememberState() {
        const state = this.stateSubject.getValue();

        this.setLocalStorageState(state);

        if (!this.ttPersistLocalOnly) {
            this.remember.remember(this.ttRememberId, state);
        }
    }

    private setLocalStorageState(state: boolean) {
        const states = this.getLocalStorageState();

        states[this.ttRememberId] = state;

        localStorage.setItem('tt-pagination', JSON.stringify(states));
    }

    private getLocalStorageState(): { [key: string]: boolean } {
        const json = localStorage.getItem('tt-pagination') ?? '{}';

        try {
            return JSON.parse(json);
        } catch (error) {
            return {};
        }
    }

    private async getAndSetLastUsedState() {
        const state = this.getLocalStorageState()[this.ttRememberId];

        if (typeof state === 'boolean') {
            if (state) {
                this.ttInitialState = 'open';
            } else {
                this.ttInitialState = 'close';
            }
        }

        if (!this.ttPersistLocalOnly) {
            try {
                const dbJsonState = (await this.remember.getLastStatus(this.ttRememberId))[0].variablevalue;
                const dbState = JSON.parse(dbJsonState);

                if (typeof dbState === 'boolean') {
                    if (state) {
                        this.ttInitialState = 'open';
                    } else {
                        this.ttInitialState = 'close';
                    }
                }
            } catch (error) {
                console.error(error);
            }
        }
    }
}
